import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { AlertTriangle, Check, Copy, KeyRound } from 'lucide-react';
import mcpServerService from '../../../services/mcpServerService';
import { copyTextToClipboard } from '../../../utils/clipboard';
import ApiKeyRevealDialog from '../../shared/ApiKeyRevealDialog';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from '../McpServerDetail.module.scss';
import {
  CLIENT_COMPATIBILITY,
  DEFAULT_USER_KEY_HEADER,
  INSTALL_TARGETS,
  describeError,
  snippetToText,
  substituteToken,
} from '../mcpUtils';

/**
 * Some install blocks are `{instructions, url}` rather than a raw snippet —
 * the instructions belong above the box as guidance, and the URL is the thing
 * that gets copied.
 */
const resolveSnippet = (raw, fallbackNote) => {
  if (!raw) return { value: null, note: fallbackNote };
  if (typeof raw === 'object' && !Array.isArray(raw) && raw.instructions) {
    return { value: raw.url || raw, note: raw.instructions };
  }
  return { value: raw, note: fallbackNote };
};

/** One copyable install snippet. */
const SnippetCard = ({ label, note, value, onCopyFailed }) => {
  const [copied, setCopied] = useState(false);
  const text = snippetToText(value);
  if (!text) return null;

  const handleCopy = async () => {
    try {
      await copyTextToClipboard(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      onCopyFailed?.();
    }
  };

  return (
    <div className={styles.snippetCard}>
      <div className={styles.snippetHead}>
        <span className={styles.snippetLabel}>{label}</span>
        <Button
          className={sharedStyles.outlineBtn}
          variant="outlined"
          size="small"
          startIcon={copied ? <Check size={15} /> : <Copy size={15} />}
          onClick={handleCopy}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      {note && <span className={styles.snippetNote}>{note}</span>}
      <pre className={styles.codeBlock}>{text}</pre>
    </div>
  );
};

/**
 * Connect (§8) — the screen that decides whether the connector actually gets
 * used. Most setup failures are copy-paste errors rather than server problems,
 * so every target gets its full snippet and a one-click copy.
 */
const ConnectTab = ({ server, install: installFromShell, tokens, onTokensChanged, notify }) => {
  const [install, setInstall] = useState(installFromShell || null);
  const [loading, setLoading] = useState(!installFromShell);
  const [error, setError] = useState(null);
  const [minting, setMinting] = useState(false);
  const [mintedToken, setMintedToken] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await mcpServerService.getInstall(server.id);
      setInstall(data);
      setError(null);
    } catch (e) {
      setError(describeError(e, "Couldn't load the install instructions."));
    } finally {
      setLoading(false);
    }
  }, [server.id]);

  // The shell already fetches this for the header URL — only go get it
  // ourselves if it wasn't handed down.
  useEffect(() => {
    if (installFromShell) {
      setInstall(installFromShell);
      setLoading(false);
      return;
    }
    load();
  }, [installFromShell, load]);

  // Sign-in mode: the end user authenticates with the customer's own provider,
  // so there is nothing here to hand out — `requires_token` and
  // `requires_user_key` both come back false and only the URL is shown.
  const usesOauth = install?.uses_oauth === true;
  const oauthIssuer = install?.oauth_issuer || '';

  const requiresToken =
    !usesOauth && (install?.requires_token ?? server?.inbound_auth_mode === 'token');
  const activeTokens = (tokens || []).filter((t) => !t.revoked_at);

  // Per-user: every end user supplies their own upstream key in this header.
  // There is no fallback to an owner key — that would show one person another
  // person's data, which is the whole point of the mode.
  // `usesOauth` overrides both of these flags rather than trusting all three to
  // agree: a sign-in server has nothing to paste anywhere, so the rule that
  // only the URL is shown is enforced here, once.
  const requiresUserKey = !usesOauth && install?.requires_user_key === true;
  const userKeyHeader = install?.user_key_header || DEFAULT_USER_KEY_HEADER;
  const isBasic = server?.upstream_auth_type === 'BASIC_AUTH';

  // URL mode: the key rides in the query string for clients that can't send
  // custom headers (Claude's website connector screen being the reason it
  // exists). A link carrying a key is inherently more exposed than a header.
  const allowsUrlCredential = !usesOauth && install?.allows_url_credential === true;
  const urlWithKey = install?.url_with_key || null;
  const claudeWeb = install?.claude_web || null;

  const userInstructions = [
    '1. Open Claude → Settings → Connectors → Add custom connector',
    `2. URL: ${install?.url || '<your server URL>'}`,
    '3. Request headers:',
    ...(requiresToken ? [`     Authorization: Bearer <the access token you were given>`] : []),
    `     ${userKeyHeader}: <${isBasic ? 'your own username:password' : 'your own API key'}>`,
    '4. Save',
  ].join('\n');

  const handleMint = async () => {
    setMinting(true);
    try {
      const result = await mcpServerService.mintToken(server.id);
      // Plaintext arrives exactly once and there is no retrieval endpoint —
      // hold it in local state only, never re-fetch it.
      setMintedToken(result?.token || null);
      await onTokensChanged?.();
      if (!result?.token) {
        notify('Token created, but the server did not return its value.', 'warning');
      }
    } catch (e) {
      notify(describeError(e, "Couldn't create a token."), 'error');
    } finally {
      setMinting(false);
    }
  };

  // Substitute the real token into the snippets at mint time — the API returns
  // the YOUR_TOKEN placeholder because it does not retain the token.
  const mintedInstall = useMemo(
    () => (mintedToken && install ? substituteToken(install, mintedToken) : null),
    [mintedToken, install]
  );

  const copyFailed = () => notify('Copy failed — select the text and copy it manually.', 'warning');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  return (
    <>
      {error && <Alert severity="error">{error}</Alert>}

      {/* ── Sign-in: replaces the whole token section. Nothing is issued here,
            and there is no header or ?key= for anyone to copy. ── */}
      {usesOauth && (
        <div className={styles.section}>
          <Typography className={styles.sectionTitle}>Your users sign in</Typography>
          <span className={styles.sectionHint}>
            Share the URL below. Your users click <strong>Connect</strong> and sign in
            {oauthIssuer ? (
              <>
                {' '}
                at <code className={styles.reference}>{oauthIssuer}</code>
              </>
            ) : (
              ' with your own login system'
            )}
            . They don’t need a key.
          </span>
          {install?.url && (
            <SnippetCard
              label="Server URL"
              note="This is the whole thing — no token, no header, no key in the link."
              value={install.url}
              onCopyFailed={copyFailed}
            />
          )}
        </div>
      )}

      {/* ── Token ── */}
      {!usesOauth && (
        <div className={styles.section}>
          <Typography className={styles.sectionTitle}>Access token</Typography>
          {requiresToken ? (
            <>
              <span className={styles.sectionHint}>
                This server requires a token. The value is shown exactly once when it is created —
                there is no way to retrieve it afterwards, so copy it before closing the dialog.
              </span>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  className={sharedStyles.primaryBtn}
                  variant="contained"
                  startIcon={minting ? <CircularProgress size={15} color="inherit" /> : <KeyRound size={16} />}
                  onClick={handleMint}
                  disabled={minting}
                >
                  {minting ? 'Creating…' : 'Create a token'}
                </Button>
                <span className={sharedStyles.muted}>
                  {activeTokens.length === 0
                    ? 'No active tokens yet.'
                    : `${activeTokens.length} active ${activeTokens.length === 1 ? 'token' : 'tokens'} — manage them in Settings.`}
                </span>
              </Box>
            </>
          ) : (
            <Alert severity="warning" icon={<AlertTriangle size={18} />}>
              This server accepts anonymous calls — no token needed. Anyone who finds the URL can
              use it, and crawlers will find it. Switch to token auth in Settings if the data behind
              it isn’t public.
            </Alert>
          )}
        </div>
      )}

      {/* ── Per-user keys: the moment the customer realises they must tell
            their users something. ── */}
      {requiresUserKey && (
        <div className={styles.section}>
          <Typography className={styles.sectionTitle}>Each person adds their own key</Typography>
          <span className={styles.sectionHint}>
            Share this link with your users. Each of them adds it in their own Claude / Cursor
            settings along with <strong>their own</strong> API key in the{' '}
            <code className={styles.reference}>{userKeyHeader}</code> header. They will only see
            their own data.
          </span>
          <Alert severity="info">
            There is no shared fallback key. If someone leaves the header out, adding the connector
            fails and tells them which header is missing — it never quietly uses your key.
          </Alert>

          <SnippetCard
            label="Instructions to send your users"
            note="Copy this and paste it into your onboarding docs or an email."
            value={userInstructions}
            onCopyFailed={copyFailed}
          />
        </div>
      )}

      {/* ── Key-in-the-URL: the primary copy target when enabled, because it is
            the only thing that works in clients without custom headers. ── */}
      {allowsUrlCredential && urlWithKey && (
        <div className={styles.section}>
          <Typography className={styles.sectionTitle}>Link with the key in it</Typography>
          <span className={styles.sectionHint}>
            Each person replaces <code className={styles.reference}>YOUR_OWN_API_KEY</code> with
            their own key. Use this for apps that can’t send custom headers.
          </span>
          <SnippetCard
            label="Server URL with key"
            note="The key is part of this link — treat it as replaceable and easy to rotate."
            value={urlWithKey}
            onCopyFailed={copyFailed}
          />

          {claudeWeb &&
            (claudeWeb.warning ? (
              <div className={`${styles.snippetCard} ${styles.snippetDisabled}`}>
                <div className={styles.snippetHead}>
                  <span className={styles.snippetLabel}>Claude website</span>
                  <span className={`${styles.badge} ${styles.badgeWarning}`}>not supported</span>
                </div>
                <span className={styles.snippetNote}>{claudeWeb.warning}</span>
              </div>
            ) : (
              <SnippetCard
                label="Claude website"
                note={claudeWeb.instructions}
                value={claudeWeb.url}
                onCopyFailed={copyFailed}
              />
            ))}
        </div>
      )}

      {/* ── Install snippets ── */}
      <div className={styles.section}>
        <Typography className={styles.sectionTitle}>Add it to your client</Typography>
        <span className={styles.sectionHint}>
          {usesOauth
            ? 'Nothing to fill in — each person signs in the first time they connect.'
            : requiresToken
            ? 'Replace YOUR_TOKEN with the token you created. Snippets copied from the token dialog already have it filled in.'
            : 'No token needed — paste and go.'}
          {requiresUserKey &&
            ` Each snippet also carries the ${userKeyHeader} header — every user replaces that value with their own key.`}
        </span>
        {/* Already the headline of the sign-in section above. */}
        {install?.url && !usesOauth && (
          <SnippetCard label="Server URL" note={null} value={install.url} onCopyFailed={copyFailed} />
        )}
      </div>

      {INSTALL_TARGETS.map((target) => {
        const raw = install?.[target.key];
        // Our own notes talk about pasting a token, which is wrong — and a dead
        // end — on a sign-in server. Anything the API says still wins below.
        const { value, note } = resolveSnippet(raw, usesOauth ? target.oauthNote || null : target.note);
        if (!value) return null;

        // A `note` on a snippet means the API is telling us that target can't
        // express this configuration — e.g. the Anthropic MCP connector sends
        // only one credential, so it can't carry both an access token and a
        // per-user key. Show the reason and stop offering it as a copy target.
        const unsupportedNote = raw && typeof raw === 'object' ? raw.note : null;
        if (unsupportedNote) {
          return (
            <div key={target.key} className={`${styles.snippetCard} ${styles.snippetDisabled}`}>
              <div className={styles.snippetHead}>
                <span className={styles.snippetLabel}>{target.label}</span>
                <span className={`${styles.badge} ${styles.badgeWarning}`}>not supported</span>
              </div>
              <span className={styles.snippetNote}>{unsupportedNote}</span>
            </div>
          );
        }

        return (
          <SnippetCard
            key={target.key}
            label={target.label}
            note={note}
            value={value}
            onCopyFailed={copyFailed}
          />
        );
      })}

      {/* ── Client compatibility (§10) ── */}
      <div className={styles.section}>
        <Typography className={styles.sectionTitle}>What works where</Typography>
        <div className={sharedStyles.tableCard}>
          {CLIENT_COMPATIBILITY.map((row) => {
            // ChatGPT's limitation is authentication of any kind, not tokens
            // specifically — a sign-in server is authenticated too, and its
            // JWT-bearer setup is not the OAuth 2.1 + DCR flow ChatGPT needs.
            const blockedByToken = row.supported === 'partial' && (requiresToken || usesOauth);
            return (
              <div key={row.client} className={styles.compatRow}>
                <span className={styles.compatClient}>{row.client}</span>
                <span>
                  {row.supported === true && <span className={`${styles.badge} ${styles.badgeNeutral}`}>works</span>}
                  {row.supported === 'partial' && (
                    <span className={`${styles.badge} ${styles.badgeWarning}`}>limited</span>
                  )}
                </span>
                <span className={`${styles.compatNote} ${blockedByToken ? styles.compatBlocked : ''}`}>
                  {blockedByToken ? row.unavailableWhenTokenRequired : row.note}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* One-time token reveal. Backdrop and Esc are disabled — losing this to a
          stray click means minting a new token. */}
      <ApiKeyRevealDialog
        open={!!mintedToken}
        apiKey={mintedToken || ''}
        title="MCP access token created"
        subtitle="There is no way to retrieve it later. Copy it now, or you'll need to create another."
        requireExplicitDismiss
        onClose={() => setMintedToken(null)}
      >
        {mintedInstall && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              READY TO PASTE — TOKEN ALREADY FILLED IN
            </Typography>
            {INSTALL_TARGETS.slice(0, 3).map((target) => {
              const { value } = resolveSnippet(mintedInstall[target.key], null);
              if (!value) return null;
              return (
                <SnippetCard
                  key={target.key}
                  label={target.label}
                  note={null}
                  value={value}
                  onCopyFailed={copyFailed}
                />
              );
            })}
          </Box>
        )}
      </ApiKeyRevealDialog>
    </>
  );
};

export default ConnectTab;
